import React, {useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Alert,
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import {useSnackbar} from 'notistack';

import {useApplicationContext} from 'ApplicationContext';
import {settingsApi} from 'Pages/Admin/Settings/settingsApi';
import {apiErrorMessage} from 'Pages/Calendar/errorText';
import {LabelEmployee} from 'Pages/Tasks/labelTaskTypes';
import {useLookup} from 'Pages/Tasks/labelwinMasterData';

import {
    FinishSupportSessionResponse,
    SupportSession,
    SupportSessionOutcome,
    elapsedSeconds,
    formatDuration,
    measuredHours,
} from './supportSessionTypes';

interface Props {
    /** Die abzuschließende Sitzung; `null` = Dialog zu. */
    session: SupportSession | null;
    onClose: () => void;
    /** Nach dem Abschluss — die Seite lädt neu. */
    onFinished: (result: FinishSupportSessionResponse) => void;
}

/**
 * Den Fall abschließen — der Schritt, der aus der Messung einen Satz in Labelwin macht.
 *
 * Drei Wege, weil Labelwin drei verschiedene Dinge kennt:
 * - **Notiz**: nichts nach Labelwin. Der Zwei-Minuten-Anruf, bei dem eine Aufgabe mehr Aufwand
 *   wäre als die Arbeit selbst.
 * - **Aufgabe**: es geht weiter, aber es wird nichts abgerechnet — Zeit hängt am Auftrag, nicht an
 *   der Aufgabe, also wird hier auch nichts gebucht.
 * - **Auftrag**: der abrechenbare Weg. Entweder auf einen vorhandenen Auftrag buchen oder über eine
 *   neue Aufgabe einen neuen Auftrag anlegen; Letzteres braucht den Kunden an der Sitzung.
 */
export const SessionFinishDialog: React.FC<Props> = ({session, onClose, onFinished}) => {
    const {t} = useTranslation();
    const context = useApplicationContext();
    const {enqueueSnackbar} = useSnackbar();
    const http = useMemo(() => settingsApi(context.api), [context.api]);

    const canBookTime = context.hasPermission('labelwin.time.book');
    const canCreateOrder = context.hasPermission('labelwin.orders.create');
    const canAssign = context.hasPermission('labelwin.tasks.assign');

    const hourTypes = useLookup(context.api, 'HourTypes');
    const activities = useLookup(context.api, 'Activities');

    const [outcome, setOutcome] = useState<SupportSessionOutcome>(SupportSessionOutcome.Note);
    const [orderNumber, setOrderNumber] = useState('');
    const [hours, setHours] = useState('');
    const [work, setWork] = useState('');
    const [hourTypeId, setHourTypeId] = useState<number | ''>('');
    const [activityId, setActivityId] = useState<number | ''>('');
    const [assignee, setAssignee] = useState<LabelEmployee | null>(null);
    const [employees, setEmployees] = useState<LabelEmployee[]>([]);
    const [completeTask, setCompleteTask] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (session === null) return;
        // Vorbelegt mit dem Weg, der am wenigsten anlegt — wer abrechnen will, wählt bewusst.
        setOutcome(SupportSessionOutcome.Note);
        setOrderNumber(session.orderNumber ?? '');
        setHours(String(measuredHours(session)));
        setWork(session.notes ?? '');
        setHourTypeId('');
        setActivityId('');
        setAssignee(null);
        setCompleteTask(false);
    }, [session]);

    useEffect(() => {
        if (!canAssign) return;
        http.get<LabelEmployee[]>('/Tasks/Employees')
            .then((r) => setEmployees(r.data))
            .catch(() => setEmployees([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canAssign]);

    const finish = async (): Promise<void> => {
        if (session === null) return;
        setBusy(true);
        try {
            const r = await http.post<FinishSupportSessionResponse>(
                `/SupportSessions/${session.id}/finish`, undefined, {
                    outcome,
                    orderNumber: outcome === SupportSessionOutcome.Order && orderNumber.trim()
                        ? orderNumber.trim()
                        : null,
                    hours: outcome === SupportSessionOutcome.Order && hours ? Number(hours) : null,
                    work: work.trim() || null,
                    hourTypeId: hourTypeId === '' ? null : hourTypeId,
                    assignEmployeeId: assignee?.employeeId ?? null,
                    activityId: activityId === '' ? null : activityId,
                    completeTask,
                });
            enqueueSnackbar(r.data.message, {variant: 'success'});
            onFinished(r.data);
            onClose();
        } catch (e) {
            enqueueSnackbar(apiErrorMessage(e, t('supportSessions.finishFailed')), {variant: 'error'});
        } finally {
            setBusy(false);
        }
    };

    const needsCustomer = outcome === SupportSessionOutcome.Order
        && orderNumber.trim().length === 0
        && (session?.customerId ?? null) === null;

    const valid = session !== null
        && (outcome !== SupportSessionOutcome.Order || (work.trim().length > 0 && !needsCustomer))
        && (outcome !== SupportSessionOutcome.Task || (session.subject ?? '').trim().length > 0);

    return (
        <Dialog open={session !== null} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
            <DialogTitle>{t('supportSessions.finishTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{mt: 1}}>
                    {session && (
                        <Typography variant="body2" color="text.secondary">
                            {t('supportSessions.measured', {
                                duration: formatDuration(elapsedSeconds(session)),
                                hours: measuredHours(session),
                            })}
                        </Typography>
                    )}

                    <ToggleButtonGroup exclusive size="small" value={outcome}
                                       onChange={(_, next) => next !== null && setOutcome(next)}>
                        <ToggleButton value={SupportSessionOutcome.Note}>
                            {t('supportSessions.outcome.note')}
                        </ToggleButton>
                        <ToggleButton value={SupportSessionOutcome.Task}>
                            {t('supportSessions.outcome.task')}
                        </ToggleButton>
                        <ToggleButton value={SupportSessionOutcome.Order}
                                      disabled={!canBookTime}>
                            {t('supportSessions.outcome.order')}
                        </ToggleButton>
                    </ToggleButtonGroup>

                    {outcome === SupportSessionOutcome.Note && (
                        <Alert severity="info">{t('supportSessions.noteHint')}</Alert>
                    )}

                    {outcome === SupportSessionOutcome.Task && (
                        <>
                            <Alert severity="info">{t('supportSessions.taskHint')}</Alert>
                            {(session?.subject ?? '').trim().length === 0 && (
                                <Alert severity="warning">{t('supportSessions.subjectMissing')}</Alert>
                            )}
                        </>
                    )}

                    {outcome === SupportSessionOutcome.Order && (
                        <>
                            {!canBookTime && (
                                <Alert severity="warning">{t('timeTracking.notAllowed')}</Alert>
                            )}
                            <TextField fullWidth size="small"
                                       label={t('supportSessions.field.orderNumber')}
                                       value={orderNumber}
                                       onChange={(e) => setOrderNumber(e.target.value)}
                                       helperText={t('supportSessions.orderNumberHint')}/>

                            {needsCustomer && (
                                <Alert severity="warning">{t('supportSessions.customerMissing')}</Alert>
                            )}
                            {orderNumber.trim().length === 0 && !needsCustomer && !canCreateOrder && (
                                <Alert severity="warning">{t('supportSessions.orderNotAllowed')}</Alert>
                            )}

                            <TextField fullWidth size="small" type="number"
                                       label={t('supportSessions.field.hours')}
                                       value={hours}
                                       slotProps={{htmlInput: {min: 0, step: 0.25}}}
                                       onChange={(e) => setHours(e.target.value)}
                                       helperText={t('supportSessions.hoursHint')}/>

                            {hourTypes.length > 0 && (
                                <TextField select fullWidth size="small"
                                           label={t('timeTracking.field.hourType')}
                                           value={hourTypeId}
                                           onChange={(e) => setHourTypeId(
                                               e.target.value === '' ? '' : Number(e.target.value))}>
                                    <MenuItem value="">{t('timeTracking.useDefault')}</MenuItem>
                                    {hourTypes.map((value) => (
                                        <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                                    ))}
                                </TextField>
                            )}
                        </>
                    )}

                    {outcome !== SupportSessionOutcome.Note && (
                        <>
                            <TextField fullWidth size="small" label={t('supportSessions.field.work')}
                                       multiline minRows={3}
                                       value={work}
                                       onChange={(e) => setWork(e.target.value)}
                                       helperText={t('supportSessions.workHint')}/>

                            {canAssign && (
                                <Autocomplete
                                    size="small"
                                    options={employees}
                                    value={assignee}
                                    onChange={(_, value) => setAssignee(value)}
                                    getOptionLabel={(option) =>
                                        option.name ?? option.labelUser ?? String(option.employeeId)}
                                    isOptionEqualToValue={(a, b) => a.employeeId === b.employeeId}
                                    renderInput={(params) => (
                                        <TextField {...params} label={t('labelTasks.field.assignee')}
                                                   helperText={t('labelTasks.assigneeHint')}/>
                                    )}
                                />
                            )}

                            {activities.length > 0 && (
                                <TextField select fullWidth size="small"
                                           label={t('labelTasks.field.activity')}
                                           value={activityId}
                                           onChange={(e) => setActivityId(
                                               e.target.value === '' ? '' : Number(e.target.value))}>
                                    <MenuItem value="">{t('labelTasks.useDefault')}</MenuItem>
                                    {activities.map((value) => (
                                        <MenuItem key={value.id} value={value.id}>{value.name}</MenuItem>
                                    ))}
                                </TextField>
                            )}

                            <FormControlLabel
                                control={<Checkbox checked={completeTask}
                                                   onChange={(e) => setCompleteTask(e.target.checked)}/>}
                                label={t('supportSessions.completeTask')}/>
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>{t('supportSessions.cancel')}</Button>
                {/*
                  * Während des Abschlusses gesperrt: es entstehen Sätze im ERP, und eine
                  * Zeitbuchung lässt sich dort nicht widerrufen.
                  */}
                <Button variant="contained" onClick={() => void finish()} disabled={busy || !valid}>
                    {t('supportSessions.finish')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SessionFinishDialog;
